import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Section } from '../templates/Section/Section';
import { localToday } from '../livestock/livestockDeceased';
import { CycleTestDialog } from './CycleTestDialog';
import {
  dismissSetupPrompt,
  getMaturity,
  maturityKey,
  setCycleOverride,
  setSetupDate,
  type MaturityStatus,
} from '../../services/maturityService';
import './NewTankSection.css';

function formatValue(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(value);
}

/** One-time "when was your tank set up?" card (#1825). */
function SetupDateCard({ aquariumId, onUpdated }: { aquariumId: string; onUpdated: (s: MaturityStatus) => void }) {
  const { t } = useTranslation();
  const [today] = useState(localToday);
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => setSetupDate(aquariumId, date), onSuccess: onUpdated });
  const dismiss = useMutation({ mutationFn: () => dismissSetupPrompt(aquariumId), onSuccess: onUpdated });
  return (
    <Card data-testid="setup-date-card">
      <Stack gap="3">
        <Heading level={3}>{t('newTank.setup.title')}</Heading>
        <Text>{t('newTank.setup.body')}</Text>
        {(save.isError || dismiss.isError) && <Alert compact>{t('newTank.saveError')}</Alert>}
        <Input
          id="new-tank-setup-date"
          type="date"
          label={t('newTank.setup.date')}
          value={date}
          max={today}
          onChange={(e) => {
            setDate(e.target.value);
            setError(null);
          }}
          error={error ?? undefined}
          disabled={save.isPending}
        />
        <Inline gap="2" wrap>
          <Button
            variant="secondary"
            onClick={() => {
              if (!date || date > today) {
                setError(t('newTank.setup.dateError'));
                return;
              }
              save.mutate();
            }}
            disabled={save.isPending}
          >
            {t('newTank.setup.save')}
          </Button>
          <Button variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
            {t('newTank.setup.notNow')}
          </Button>
        </Inline>
      </Stack>
    </Card>
  );
}

/** Cycle tracker (#1825): progress from the NH3 / NO2 tests, guided logging, user override. */
function CycleCard({ aquariumId, status, onUpdated }: { aquariumId: string; status: MaturityStatus; onUpdated: (s: MaturityStatus) => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const [testOpen, setTestOpen] = useState(false);
  const queryClient = useQueryClient();
  const override = useMutation({ mutationFn: (complete: boolean | null) => setCycleOverride(aquariumId, complete), onSuccess: onUpdated });
  const { cycle, latest } = status;
  const complete = cycle.complete;
  return (
    <Card data-testid="cycle-card">
      <Stack gap="3">
        <Inline gap="2" align="center" wrap>
          <Heading level={3}>{t('newTank.cycle.title')}</Heading>
          <Badge tone={complete ? 'success' : 'warning'} data-testid="cycle-status">
            {t(`newTank.cycle.status.${complete ? 'complete' : cycle.status}`)}
          </Badge>
        </Inline>
        <Text>
          {complete
            ? t(`newTank.cycle.doneBody.${cycle.source === 'override' ? 'override' : 'computed'}`)
            : t('newTank.cycle.body', { count: cycle.cleanSets, sets: cycle.cleanSets })}
        </Text>
        <ul className="new-tank__latest" data-testid="cycle-latest">
          {(['ammonia', 'nitrite', 'nitrate'] as const).map((p) => (
            <li key={p}>
              <Text>
                {latest[p]
                  ? t('newTank.cycle.latest', { parameter: t(`newTank.test.${p}`), value: formatValue(latest[p]!.value, language), unit: latest[p]!.unit })
                  : t('newTank.cycle.noValue', { parameter: t(`newTank.test.${p}`) })}
              </Text>
            </li>
          ))}
        </ul>
        <Text color="muted" variant="caption">{t('newTank.cycle.rule')}</Text>
        {override.isError && <Alert compact>{t('newTank.saveError')}</Alert>}
        <Inline gap="2" wrap>
          <Button variant="secondary" onClick={() => setTestOpen(true)}>{t('newTank.cycle.logTest')}</Button>
          {cycle.override === null ? (
            !complete && (
              <Button variant="ghost" onClick={() => override.mutate(true)} disabled={override.isPending}>
                {t('newTank.cycle.markComplete')}
              </Button>
            )
          ) : (
            <Button variant="ghost" onClick={() => override.mutate(null)} disabled={override.isPending}>
              {t('newTank.cycle.useTests')}
            </Button>
          )}
        </Inline>
      </Stack>
      <CycleTestDialog
        aquariumId={aquariumId}
        isOpen={testOpen}
        onClose={() => setTestOpen(false)}
        onSaved={() => {
          setTestOpen(false);
          void queryClient.invalidateQueries({ queryKey: maturityKey(aquariumId) });
          void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
        }}
      />
    </Card>
  );
}

/** What to expect in the first weeks (#1825) — so the "ugly phase" does not cause panic. */
function UglyPhaseCard() {
  const { t } = useTranslation();
  return (
    <Card data-testid="ugly-phase-card">
      <Stack gap="2">
        <Heading level={3}>{t('newTank.ugly.title')}</Heading>
        <Text>{t('newTank.ugly.intro')}</Text>
        <ul className="new-tank__phases">
          {(['bloom', 'diatoms', 'algae', 'settle'] as const).map((k) => (
            <li key={k}>
              <Text>
                <strong>{t(`newTank.ugly.${k}.when`)}</strong> {t(`newTank.ugly.${k}.what`)}
              </Text>
            </li>
          ))}
        </ul>
        <Text color="muted" variant="caption">{t('newTank.ugly.note')}</Text>
      </Stack>
    </Card>
  );
}

/**
 * New-tank mode on the dashboard (#1825): asks once for the setup date, then —
 * while the tank is cycling or young — shows the cycle tracker and what to
 * expect in the first weeks. Renders nothing for established tanks.
 */
export function NewTankSection({ aquariumId }: { aquariumId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const query = useQuery({ queryKey: maturityKey(aquariumId), queryFn: () => getMaturity(aquariumId), enabled: Boolean(aquariumId), retry: false });
  const status = query.data;
  if (!status) return null;
  const onUpdated = (s: MaturityStatus) => {
    queryClient.setQueryData(maturityKey(aquariumId), s);
    setAnnouncement(t('newTank.saved'));
  };
  const showTracker = status.stage === 'cycling' || status.stage === 'young';
  if (!status.askSetupDate && !showTracker) return <LiveRegion visuallyHidden>{announcement}</LiveRegion>;

  return (
    <Section
      title={t('newTank.title')}
      description={status.ageDays !== null ? t(`newTank.stage.${status.stage}`, { days: status.ageDays }) : t('newTank.description')}
      data-testid="new-tank-section"
    >
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
      <div className="new-tank__grid">
        {status.askSetupDate && <SetupDateCard aquariumId={aquariumId} onUpdated={onUpdated} />}
        {showTracker && <CycleCard aquariumId={aquariumId} status={status} onUpdated={onUpdated} />}
        {showTracker && <UglyPhaseCard />}
      </div>
    </Section>
  );
}
